import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { User } from "@vv/types";

export type AuthStatus = "idle" | "loading" | "authenticated" | "unauthenticated";

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  status: AuthStatus;
  error: string | null;
}

const STORAGE_KEY = "template.auth";

export interface PersistedAuth {
  accessToken: string;
  refreshToken: string;
  user: User;
}

export function readPersistedAuth(): PersistedAuth | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedAuth;
  } catch {
    return null;
  }
}

export function persistAuth(auth: PersistedAuth | null): void {
  if (typeof window === "undefined") return;
  if (!auth) {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
}

const persisted = readPersistedAuth();

const initialState: AuthState = {
  user: persisted?.user ?? null,
  accessToken: persisted?.accessToken ?? null,
  refreshToken: persisted?.refreshToken ?? null,
  status: persisted ? "authenticated" : "unauthenticated",
  error: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    authRequestStarted(state) {
      state.status = "loading";
      state.error = null;
    },
    authSucceeded(
      state,
      action: PayloadAction<{ user: User; accessToken: string; refreshToken: string }>,
    ) {
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.status = "authenticated";
      state.error = null;
    },
    authFailed(state, action: PayloadAction<string>) {
      state.status = "unauthenticated";
      state.error = action.payload;
    },
    tokensRefreshed(state, action: PayloadAction<{ accessToken: string; refreshToken: string }>) {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
    },
    userLoaded(state, action: PayloadAction<User>) {
      state.user = action.payload;
      state.status = "authenticated";
    },
    loggedOut(state) {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.status = "unauthenticated";
      state.error = null;
    },
  },
});

export const {
  authRequestStarted,
  authSucceeded,
  authFailed,
  tokensRefreshed,
  userLoaded,
  loggedOut,
} = authSlice.actions;

export default authSlice.reducer;
